import { useLayoutEffect, useRef, useState } from "react";
import styles from "./TrendSparkline.module.scss";

/** One reading. `label` is the x-axis caption; only the FIRST and LAST are drawn, under the baseline. */
export interface TrendSparklinePoint {
  label: string;
  value: number;
}

export interface TrendSparklineProps {
  /** The series, oldest → newest, with the point being explained LAST. */
  points: ReadonlyArray<TrendSparklinePoint>;
  /** Pixel height of the chart. Default 72 — tall enough for the peak label above and the axis row below. */
  height?: number;
  /** Accessible name. Without one the chart is `role="presentation"` — correct where the surrounding copy
   *  already states the figure. */
  label?: string;
}

/** Quadratic-midpoint smoothing: passes through both endpoints, eases the middle, and runs the LAST leg
 *  straight into the final point so the peak sits exactly on the value rather than on a curve near it. */
function smoothPath(xs: readonly number[], ys: readonly number[]): string {
  let d = `M ${xs[0].toFixed(1)} ${ys[0].toFixed(1)}`;
  for (let i = 1; i < xs.length - 1; i++) {
    const mx = (xs[i] + xs[i + 1]) / 2;
    const my = (ys[i] + ys[i + 1]) / 2;
    d += ` Q ${xs[i].toFixed(1)} ${ys[i].toFixed(1)} ${mx.toFixed(1)} ${my.toFixed(1)}`;
  }
  return `${d} L ${xs[xs.length - 1].toFixed(1)} ${ys[ys.length - 1].toFixed(1)}`;
}

/**
 * The DETAIL trend graph — a labelled area chart that explains one moment in a series.
 *
 * A different contract from {@link Sparkline}, not a variant of it. `Sparkline` is a micro-chart: bare
 * values, a caller-fixed box, `preserveAspectRatio="none"`, no text — right for a 72×24 cell in a roster
 * row. This one is the chart you open something to look at: it MEASURES its container so strokes and type
 * stay true at any width, calls out the final reading with a halo, dot and its own value, and prints the
 * first/last labels under the baseline. Stretching that treatment through a non-uniform viewBox would skew
 * the dot into an ellipse and the type with it, which is why the measurement is not optional.
 *
 * It holds NO date semantics: points arrive pre-labelled, so the caller's own domain-date rule stays the
 * one authority on how a day is spelled.
 *
 * Absence is graceful and silent: fewer than two finite readings render nothing at all rather than a flat
 * line, which would read as "this metric did not move" instead of "we have no series". Never synthesises a
 * curve — the points ARE the numbers the caller's own arithmetic ran on.
 */
export function TrendSparkline({ points, height = 72, label }: TrendSparklineProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  // `useLayoutEffect`, so the measured width is in state BEFORE the first paint — the chart never flashes at
  // a placeholder size and then snaps. A ResizeObserver keeps it true when the dialog or column resizes.
  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth || 0);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const finite = points.filter((p) => p != null && Number.isFinite(Number(p.value)));
  if (finite.length < 2) return null;

  // Top pad clears the peak's value label, bottom pad the axis row.
  const padTop = 16;
  const padBottom = 18;
  const padX = 6;
  const floor = height - padBottom;
  const plotH = floor - padTop;
  const plotW = Math.max(width - padX * 2, 1);

  const values = finite.map((p) => Number(p.value));
  const min = Math.min(...values);
  const span = Math.max(...values) - min || 1; // a flat series still draws — as a level line, honestly
  const xs = finite.map((_, i) => padX + (i / (finite.length - 1)) * plotW);
  const ys = values.map((v) => floor - ((v - min) / span) * plotH);
  const line = smoothPath(xs, ys);
  const peakX = xs[xs.length - 1];
  const peakY = ys[ys.length - 1];

  return (
    <div ref={hostRef} className={styles.host}>
      <svg
        className={styles.chart}
        width={width || undefined}
        height={height}
        viewBox={`0 0 ${width || 0} ${height}`}
        role={label ? "img" : "presentation"}
        aria-label={label}
        focusable="false"
      >
        {/* Drawn only once measured. The shell renders regardless so the accessible name is present from the
            first frame — a chart that appears a tick later is a chart assistive tech can miss. */}
        {width > 40 && (
          <>
            <path
              className={styles.area}
              d={`${line} L ${peakX.toFixed(1)} ${floor.toFixed(1)} L ${xs[0].toFixed(1)} ${floor.toFixed(1)} Z`}
            />
            <path className={styles.line} d={line} />
            <circle className={styles.halo} cx={peakX} cy={peakY} r={7} />
            <circle className={styles.dot} cx={peakX} cy={peakY} r={3.2} />
            <text className={styles.peak} x={peakX - 2} y={Math.max(11, peakY - 8)} textAnchor="end">
              {Math.round(values[values.length - 1]).toLocaleString()}
            </text>
            <text className={styles.axis} x={padX} y={height - 4} textAnchor="start">
              {finite[0].label}
            </text>
            <text className={styles.axis} x={width - padX} y={height - 4} textAnchor="end">
              {finite[finite.length - 1].label}
            </text>
          </>
        )}
      </svg>
    </div>
  );
}
